import { APIService } from '../../api/apiService';
import type {
  AuthTokenDto,
  GroupedSessionDto,
  RevokeSessionResponse,
  RevokeSessionsResult,
} from '../../interfaces/SessionApiService';

export class SessionService extends APIService {
  private readonly basePath = 'sessions';

  /**
   * Get all active sessions for the current user
   * @returns List of active sessions with isCurrent flag on the current session
   */
  getActiveSessions(): Promise<AuthTokenDto[]> {
    return this.request(`${this.basePath}`, 'GET');
  }

  /**
   * Get all active sessions grouped by device (DeviceSign + IpAddress + ClientApplication)
   * @returns List of grouped sessions with sessionCount and ids for bulk revocation
   */
  getActiveSessionsGrouped(): Promise<GroupedSessionDto[]> {
    return this.request(`${this.basePath}/grouped`, 'GET');
  }

  /**
   * Revoke a single session
   * @param sessionId - ID of the session to revoke
   * @returns Success message
   */
  revokeSession(sessionId: number): Promise<RevokeSessionResponse> {
    return this.request(`${this.basePath}/${sessionId}`, 'DELETE');
  }

  /**
   * Revoke multiple sessions by IDs (used for grouped sessions)
   * @param sessionIds - Array of session IDs to revoke
   * @returns Result with count of revoked sessions
   */
  revokeMultipleSessions(sessionIds: number[]): Promise<RevokeSessionsResult> {
    return this.request(`${this.basePath}/revoke-multiple`, 'POST', { sessionIds });
  }

  /**
   * Revoke all sessions except the current one (logout everywhere else)
   * @returns Result with count of revoked sessions
   */
  revokeOtherSessions(): Promise<RevokeSessionsResult> {
    return this.request(`${this.basePath}/revoke-others`, 'POST');
  }

  /**
   * Revoke all sessions including the current one (complete logout)
   * @returns Result with count of revoked sessions
   */
  revokeAllSessions(): Promise<RevokeSessionsResult> {
    return this.request(`${this.basePath}/revoke-all`, 'POST');
  }

  // Admin endpoints

  /**
   * Get all active sessions in the company (admin only)
   * @returns List of all active sessions in the company
   */
  getCompanySessions(): Promise<AuthTokenDto[]> {
    return this.request(`${this.basePath}/admin/company`, 'GET');
  }

  /**
   * Get all active sessions of a specific user, grouped by device (admin only)
   * @param userId - ID of the user whose sessions to retrieve
   * @returns List of grouped sessions with sessionCount and ids
   */
  adminGetUserSessionsGrouped(userId: number): Promise<GroupedSessionDto[]> {
    return this.request(`${this.basePath}/admin/user/${userId}/grouped`, 'GET');
  }

  /**
   * Revoke all sessions of a specific user (admin only)
   * @param userId - ID of the user whose sessions to revoke
   * @returns Result with count of revoked sessions
   */
  adminRevokeUserSessions(userId: number): Promise<RevokeSessionsResult> {
    return this.request(`${this.basePath}/admin/user/${userId}`, 'DELETE');
  }

  /**
   * Revoke a specific session in the company (admin only)
   * @param sessionId - ID of the session to revoke
   * @returns Success message
   */
  adminRevokeSession(sessionId: number): Promise<RevokeSessionResponse> {
    return this.request(`${this.basePath}/admin/${sessionId}`, 'DELETE');
  }
}
